import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { AccountsReceivableService, AccountsReceivablePagedResult, AccountsReceivableFilters } from '../../../services/accounts-receivable.service';
import { CustomersService } from '../../../services/customers.service';
import { CostCentersService } from '../../../services/cost-centers.service';
import { StatusService } from '../../../services/status.service';
import { AccountsReceivable } from '../../../models/AccountsReceivable';
import { Customer } from '../../../models/Customer';
import { CostCenter } from '../../../models/CostCenter';
import { Status } from '../../../models/Status';
import { TableAction } from '../../../shared/components/table/table.component';
import { canReceiveAccountsReceivable } from '../../../utils/accounts-receivable.utils';

@Component({
  selector: 'app-accounts-receivable',
  templateUrl: './accounts-receivable.component.html',
  styleUrls: ['./accounts-receivable.component.css']
})
export class AccountsReceivableComponent implements OnInit, OnDestroy {
  // Configuração da tabela
  tableTitle = 'Gestão de Contas a Receber';
  showSearch = true;
  searchPlaceholder = 'Buscar contas a receber...';
  clickable = true;

  // Colunas da tabela
  columns = [
    { titulo: 'Documento', label: 'documentNumber' },
    { titulo: 'Tipo', label: 'documentType' },
    { titulo: 'Cliente', label: 'customerName' },
    { titulo: 'Contrato', label: 'contractNumber' },
    { titulo: 'Centro de Custo', label: 'costCenterName' },
    { titulo: 'Valor a Receber', label: 'amountToReceive', pipe: 'currency' },
    { titulo: 'Vencimento', label: 'dueDate', pipe: 'date' },
    { titulo: 'Status', label: 'status', badge: true },
    { titulo: 'Tipo Recebimento', label: 'receiptType' }
  ];

  // Ações da tabela
  actions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  // Dados
  accountsReceivable: AccountsReceivable[] = [];
  customers: Customer[] = [];
  costCenters: CostCenter[] = [];
  selectedAccountsReceivable: AccountsReceivable | null = null;
  loading = false;
  error: string | null = null;

  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  filters: AccountsReceivableFilters & { 
    selectedCustomerId: number | string | null;
    selectedStatus: string | null;
    selectedCategory: string | null;
    selectedCostCenterId: number | string | null;
    dueStartDate: string;
    dueEndDate: string;
    receiptStartDate: string;
    receiptEndDate: string;
    searchTerm: string;
  } = {
    selectedCustomerId: '',
    selectedStatus: '',
    selectedCategory: '',
    selectedCostCenterId: '',
    dueStartDate: '',
    dueEndDate: '',
    receiptStartDate: '',
    receiptEndDate: '',
    searchTerm: '',
    page: 1,
    pageSize: 10
  };
  loadingStatuses = false;
  
  // Timeout para debounce da busca
  private searchTimeout: any;

  // Opções para filtros
  statusOptions: Status[] = [];

  categoryOptions = [
    'Vendas',
    'Serviços',
    'Locação',
    'Manutenção',
    'Multas',
    'Juros',
    'Consultoria',
    'Treinamento',
    'Licenças',
    'Assinaturas',
    'Comissões',
    'Bonificações',
    'Reembolsos',
    'Outros'
  ];

  constructor(
    private router: Router,
    private accountsReceivableService: AccountsReceivableService,
    private customersService: CustomersService,
    private costCentersService: CostCentersService,
    private statusService: StatusService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadCustomers();
    this.loadCostCenters();
    this.loadAccountsReceivable();
    this.loadAccountsReceivableStatuses();
  }

  loadCustomers(): void {
    this.customersService.getAll().subscribe({
      next: (customers: Customer[]) => {
        this.customers = customers || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar clientes:', error);
        this.customers = [];
      }
    });
  }

  loadCostCenters(): void {
    this.costCentersService.getActive().subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar centros de custo:', error);
        this.costCenters = [];
      }
    });
  }

  loadAccountsReceivable(): void {
    this.loading = true;
    this.error = null;

    // Prepara os filtros removendo valores vazios
    const filters: AccountsReceivableFilters = {
      page: this.filters.page || this.currentPage || 1,
      pageSize: this.filters.pageSize || this.pageSize || 10
    };
    
    if (this.filters.selectedCustomerId && this.filters.selectedCustomerId !== '' && Number(this.filters.selectedCustomerId) > 0) {
      filters.customerId = Number(this.filters.selectedCustomerId);
    }
    if (this.filters.selectedStatus && this.filters.selectedStatus.trim()) {
      filters.status = this.filters.selectedStatus.trim();
    }
    if (this.filters.selectedCostCenterId && this.filters.selectedCostCenterId !== '' && Number(this.filters.selectedCostCenterId) > 0) {
      filters.costCenterId = Number(this.filters.selectedCostCenterId);
    }
    if (this.filters.selectedCategory && this.filters.selectedCategory.trim()) {
      filters.category = this.filters.selectedCategory.trim();
    }
    if (this.filters.dueStartDate && this.filters.dueStartDate.trim()) {
      filters.dueDateStart = this.filters.dueStartDate.trim();
    }
    if (this.filters.dueEndDate && this.filters.dueEndDate.trim()) {
      filters.dueDateEnd = this.filters.dueEndDate.trim();
    }
    if (this.filters.receiptStartDate && this.filters.receiptStartDate.trim()) {
      filters.receiptDateStart = this.filters.receiptStartDate.trim();
    }
    if (this.filters.receiptEndDate && this.filters.receiptEndDate.trim()) {
      filters.receiptDateEnd = this.filters.receiptEndDate.trim();
    }
    if (this.filters.searchTerm && this.filters.searchTerm.trim()) {
      filters.searchTerm = this.filters.searchTerm.trim();
    }

    this.accountsReceivableService.getAllAccountsReceivable(filters).subscribe({
      next: (result: AccountsReceivablePagedResult) => {
        this.accountsReceivable = this.decorateAccountsReceivable(result.items || []);
        this.totalItems = result.totalItems;
        this.totalPages = result.totalPages;
        this.currentPage = result.page;
        this.pageSize = result.pageSize;
        this.filters.page = result.page;
        this.filters.pageSize = result.pageSize;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar contas a receber:', error);
        this.error = `Erro ao carregar contas a receber: ${error.message || 'Erro desconhecido'}`;
        this.loading = false;
        this.accountsReceivable = [];
      }
    });
  }

  onSearchChange(searchTerm: string): void {
    // Limpar timeout anterior se o usuário continuar digitando
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Atualiza o termo de busca nos filtros
    this.filters.searchTerm = searchTerm || '';
    
    // Aguardar 600ms após parar de digitar antes de fazer a requisição
    this.searchTimeout = setTimeout(() => {
      this.filters.page = 1;
      this.currentPage = 1;
      this.loadAccountsReceivable();
      this.searchTimeout = null;
    }, 600);
  }

  onCustomerFilterChange(customerId: number | string | null): void {
    this.filters.selectedCustomerId = customerId;
    this.applyFilters();
  }

  onStatusFilterChange(status: string | null): void {
    this.filters.selectedStatus = status;
    this.applyFilters();
  }

  onCategoryFilterChange(category: string | null): void {
    this.filters.selectedCategory = category;
    this.applyFilters();
  }

  onCostCenterFilterChange(costCenterId: number | string | null): void {
    this.filters.selectedCostCenterId = costCenterId;
    this.applyFilters();
  }

  onDueRangeChange(): void {
    this.applyFilters();
  }

  onReceiptRangeChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    // Reset para primeira página ao aplicar filtros
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAccountsReceivable();
  }

  clearFilters(): void {
    this.filters = {
      selectedCustomerId: '',
      selectedStatus: '',
      selectedCategory: '',
      selectedCostCenterId: '',
      dueStartDate: '',
      dueEndDate: '',
      receiptStartDate: '',
      receiptEndDate: '',
      searchTerm: '',
      page: 1,
      pageSize: 10
    };
    this.currentPage = 1;
    this.loadAccountsReceivable();
  }
  
  ngOnDestroy(): void {
    // Limpar timeout ao destruir componente
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  retryLoad(): void {
    this.loadAccountsReceivable();
  }

  onActionClick(actionData: {action: string, item: any}): void {
    const { action, item } = actionData;
    
    switch (action) {
      case 'view':
        this.viewAccountsReceivable(item);
        break;
      case 'edit':
        this.editAccountsReceivable(item);
        break;
      case 'receive':
        if (canReceiveAccountsReceivable(item)) {
          this.receiveAccountsReceivable(item);
        }
        break;
      case 'delete':
        this.deleteAccountsReceivable(item);
        break;
      default:
    }
  }

  viewAccountsReceivable(accountsReceivable: AccountsReceivable): void {
    this.router.navigate(['/financial/accounts-receivable', accountsReceivable.id, 'view']);
  }

  editAccountsReceivable(accountsReceivable: AccountsReceivable): void {
    this.router.navigate(['/financial/accounts-receivable', accountsReceivable.id, 'edit']);
  }

  receiveAccountsReceivable(accountsReceivable: AccountsReceivable): void {
    if (!canReceiveAccountsReceivable(accountsReceivable)) {
      return;
    }
    this.router.navigate(['/financial/accounts-receivable', accountsReceivable.id, 'view'], {
      queryParams: { action: 'receive' }
    });
  }

  private decorateAccountsReceivable(
    items: AccountsReceivable[]
  ): (AccountsReceivable & { _actions?: TableAction[] })[] {
    return (items || []).map((item) => ({
      ...item,
      _actions: this.buildRowActions(item)
    }));
  }

  private buildRowActions(ar: AccountsReceivable): TableAction[] {
    const actions: TableAction[] = [
      { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view', visible: true },
      { label: 'Editar', icon: 'fas fa-pen-to-square', action: 'edit', class: 'btn-edit', visible: true }
    ];
    if (canReceiveAccountsReceivable(ar)) {
      actions.push({
        label: 'Receber',
        icon: 'fas fa-hand-holding-usd',
        action: 'receive',
        class: 'btn-receive',
        visible: true
      });
    }
    actions.push({
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete',
      visible: true
    });
    return actions;
  }

  deleteAccountsReceivable(accountsReceivable: AccountsReceivable): void {
    const doc = accountsReceivable.documentNumber || String(accountsReceivable.id);
    this.confirmationService
      .confirmDelete(doc, 'a conta a receber')
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.accountsReceivableService.deleteAccountsReceivable(accountsReceivable.id).subscribe({
          next: () => this.loadAccountsReceivable(),
          error: (error) => console.error('Erro ao excluir conta a receber:', error)
        });
      });
  }

  newAccountsReceivable(): void { 
    this.router.navigate(['/financial/accounts-receivable/new']); 
  }

  getCustomerName(customerId?: number): string {
    if (!customerId) return 'N/A';
    const customer = this.customers.find(c => c.id === customerId);
    return customer ? customer.name : 'N/A';
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pendente':
        return 'status-pending';
      case 'recebido':
        return 'status-received';
      case 'cancelado':
        return 'status-cancelled';
      case 'vencido':
        return 'status-overdue';
      case 'parcialmente recebido':
      case 'parcial':
        return 'status-partial';
      default:
        return 'status-default';
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  loadAccountsReceivableStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('AccountsReceivable', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de contas a receber:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Pendente', description: 'Conta pendente', entityType: 'AccountsReceivable', isActive: true },
            { id: 2, name: 'Recebido', description: 'Conta recebida', entityType: 'AccountsReceivable', isActive: true },
            { id: 3, name: 'Cancelado', description: 'Conta cancelada', entityType: 'AccountsReceivable', isActive: true },
            { id: 4, name: 'Vencido', description: 'Conta vencida', entityType: 'AccountsReceivable', isActive: true },
            { id: 5, name: 'Parcial', description: 'Conta parcialmente recebida', entityType: 'AccountsReceivable', isActive: true }
          ] as Status[];
        }
      });
  }

  onPageChange(page: number): void {
    this.filters.page = page;
    this.currentPage = page;
    this.loadAccountsReceivable();
  }

  onPageSizeChange(pageSize: number): void {
    this.filters.pageSize = pageSize;
    this.pageSize = pageSize;
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAccountsReceivable();
  }
}
